import instance, { ADMIN_PREFIX } from 'services/api';
import { AdminUser } from 'types/api/profile';
import {
  AuthQrResult,
  SignInData,
  SignInInput,
  VerifyOtpData,
  VerifyOtpInput
} from 'types/api/auth';

/**
 * POST /auth/sign-in — credential exchange. Returns the tmpToken (for
 * the optional 2FA flow) plus token/refreshToken when 2FA is off.
 * withCredentials=true so the BE can set its HttpOnly session cookie.
 */
export const signIn = async (input: SignInInput): Promise<SignInData> => {
  const res = await instance.post(`${ADMIN_PREFIX}/auth/sign-in`, input, {
    withCredentials: true
  });

  return res?.data?.data ?? res?.data;
};

/**
 * POST /auth/verify-otp — dual-purpose OTP verification. Accepts
 * `{otp, token}` where `token` is either the tmpToken from the
 * sign-in flow OR the active access token when enabling 2FA from
 * the Authentication settings panel.
 */
export const verifyOtp = async (
  input: VerifyOtpInput
): Promise<VerifyOtpData> => {
  const res = await instance.post(`${ADMIN_PREFIX}/auth/verify-otp`, input);

  return res?.data?.data ?? res?.data ?? {};
};

/**
 * POST /auth/logout — JWT-scoped (Authorization header set by the
 * instance interceptor). Caller is responsible for clearing storage +
 * dispatching state cleanup.
 */
export const logout = (): Promise<unknown> =>
  instance.post(`${ADMIN_PREFIX}/auth/logout`);

/**
 * GET /auth/me — returns the calling admin's record with role,
 * roleName, and permissions pre-rolled (P20.2 closure).
 */
export const fetchMe = async (): Promise<AdminUser | null> => {
  const res = await instance.get(`${ADMIN_PREFIX}/auth/me`);

  return res?.data?.data ?? res?.data ?? null;
};

/**
 * POST /auth/qr — provisions a fresh 2FA QR + secret pair for the
 * calling admin. Used by the Authentication settings panel before
 * the OTP-verify confirmation step.
 */
export const generateAuthQr = async (): Promise<AuthQrResult> => {
  const res = await instance.post(`${ADMIN_PREFIX}/auth/qr`);

  return res?.data?.data ?? res?.data;
};

/**
 * POST <route> { email } — generic resend helper for confirmation /
 * password-reset emails. `route` is one of the unauth /auth/* paths
 * the BE exposes (forgot-password, reset-password, etc.).
 */
export const resendAuthEmail = (route: string, email: string) =>
  instance.post(route, { email });
